import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
// Store
import { createSelector, Store } from '@ngrx/store';
import { find as _find, get as _get, has as _has, merge as _merge } from 'lodash';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import { PaymentConfirmationSelectors } from '@amfam/billing/paymentconfirmation/data-access';
import {
  AutomaticPayment,
  BillingPaymentConfirmationModel,
  BillingPaymentPaths,
  PaymentAccount,
  PaymentConfirmationModel
} from '@amfam/shared/models';
import { PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { Applications, ApplicationService } from '@amfam/shared/utility/shared-services';
import { Action, DockingBarService, LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

@Component({
  templateUrl: './print-confirmation-wrapper.component.html',
  styleUrls: ['./print-confirmation-wrapper.component.scss'],
  standalone: false
})
export class PrintConfirmationWrapperComponent implements OnInit, OnDestroy {
  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('statusCheckCellTpl', { static: true }) statusCheckCellTpl: TemplateRef<unknown>;
  @ViewChild('accountNameCellTpl', { static: true }) accountNameCellTpl: TemplateRef<unknown>;
  @ViewChild('billAccountNumberCellTpl', { static: true })
  billAccountNumberCellTpl: TemplateRef<unknown>;
  @ViewChild('policyDescriptionCellTpl', { static: true })
  policyDescriptionCellTpl: TemplateRef<unknown>;
  @ViewChild('riskDescCellTpl', { static: true }) riskDescCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentAccountCellTpl', { static: true }) paymentAccountCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentAccountNickNameOnlyCellTpl')
  paymentAccountNickNameOnlyCellTpl: TemplateRef<unknown>;
  @ViewChild('usdPaymentAmountOnlyCellTpl', { static: true })
  usdPaymentAmountOnlyCellTpl: TemplateRef<unknown>;
  @ViewChild('usdPaymentAmountWithDateCellTpl', { static: true })
  usdPaymentAmountWithDateCellTpl: TemplateRef<unknown>;
  @ViewChild('mmddyyyyPaymentDateCellTpl', { static: true })
  mmddyyyyPaymentDateCellTpl: TemplateRef<unknown>;
  @ViewChild('relativePaymentDateCellTpl', { static: true })
  relativePaymentDateCellTpl: TemplateRef<unknown>;
  @ViewChild('minOrFullPaymentAmountOnlyCellTpl', { static: true })
  minOrFullPaymentAmountOnlyCellTpl: TemplateRef<unknown>;
  @ViewChild('minOrFullPaymentAmountWithDaysBeforeCellTpl', { static: true })
  minOrFullPaymentAmountWithDaysBeforeCellTpl: TemplateRef<unknown>;
  @ViewChild('confirmationNumberCellTpl', { static: true })
  confirmationNumberCellTpl: TemplateRef<unknown>;

  confirmationObj: BillingPaymentConfirmationModel;

  private paymentMethodConfirmationHeaderTxt = 'Billing & Payments';

  private paymentAccounts: PaymentAccount[] = [];
  private autoPayAccounts: AutomaticPayment[] = [];
  private isMyAccountAdmin: boolean;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private pageTitle: PageTitleService,
    private dockingService: DockingBarService,
    private store: Store,
    public spinner: LoadingSpinnerService,
    private applicationService: ApplicationService
  ) {}

  ngOnInit() {
    this.isMyAccountAdmin = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
    this.dockingService.registerHeading(this.paymentMethodConfirmationHeaderTxt);
    if (this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) {
      this.store
        .select(userQuery.selectFirstAndLastName)
        .pipe(
          filter(name => !!name),
          take(1)
        )
        .subscribe(name => {
          this.dockingService.registerSecondaryHeading(`${name}`);
        });
    }

    this.dockingService.registerAction(
      new Action('Print Confirmation', () => {
        window.print();
      })
    );
    this.pageTitle.set(this.paymentMethodConfirmationHeaderTxt);

    const selectLoading = createSelector(
      AutoPaySelectors.getAnyLoading,
      PaymentAccountSelectors.selectPaymentAccountsLoading,
      PaymentMethodSelectors.selectLoading,
      BillAccountsSelectors.selectBillAccountsAnyLoading,
      PaymentConfirmationSelectors.selectPaymentConfirmationLoading,
      (
        automaticPaymentsLoading,
        paymentAccountsLoading,
        paymentMethodsLoading,
        billAccountsLoading,
        confirmationsLoading
      ) =>
        automaticPaymentsLoading ||
        paymentAccountsLoading ||
        billAccountsLoading ||
        paymentMethodsLoading ||
        confirmationsLoading
    );

    // Pin the loading spinner to the loading state
    this.store
      .select(selectLoading)
      .pipe(takeUntil(this.stop$))
      .subscribe(loading => {
        if (loading) {
          this.spinner.start({
            blockActions: true
          });
        } else {
          this.spinner.stop();
        }
      });

    this.store
      .select(
        createSelector(
          PaymentConfirmationSelectors.selectPaymentConfirmationDataState,
          PaymentAccountSelectors.selectPaymentAccountsWithoutUnavailable,
          PaymentMethodSelectors.selectPaymentMethods,
          AutoPaySelectors.getAutomaticPayments,
          selectLoading,
          (
            paymentConfirmationObj,
            paymentAccounts,
            paymentMethods,
            automaticPayments,
            loading
          ) => ({
            paymentConfirmationObj: paymentConfirmationObj,
            paymentAccounts: paymentMethods,
            loading: loading,
            automaticPayments: automaticPayments
          })
        )
      )
      .pipe(
        filter(state => !state.loading),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        // Early out if we don'thave a confirmation category
        if (_get(state, 'paymentConfirmationObj.category') === null) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.BILLING_SECTION_PAGE]
            })
          );
          return;
        }
        const filteredPaymentMethods = state?.paymentAccounts?.filter(function (x) {
          return x !== undefined;
        });
        this.paymentAccounts = filteredPaymentMethods;
        this.autoPayAccounts = state.automaticPayments;

        switch (state.paymentConfirmationObj.category) {
          case 'paymentMethod':
            /**
             * AS: Need to move all the implementation of confirmation to the store.
             * Right now only the implementation of payment Method confirmation screen
             * is in store.
             */

            if (state.paymentConfirmationObj) {
              this.confirmationObj = _merge(
                {},
                this.formatStoreData(<PaymentConfirmationModel>state.paymentConfirmationObj),
                this.getPaymentMethodsRowDataMap(
                  <PaymentConfirmationModel>state.paymentConfirmationObj
                )
              );
            }
            break;
          case 'scheduledPayments':
            this.confirmationObj = _merge(
              {},
              state.paymentConfirmationObj,
              this.getScheduleRowDataMap(state.paymentConfirmationObj)
            );
            break;
          case 'multipleAutomaticPaymentRule':
            this.confirmationObj = _merge(
              {},
              state.paymentConfirmationObj,
              this.getMultipleAutpayRowDataMap(state.paymentConfirmationObj)
            );
            break;
          case 'automaticPaymentRule':
            this.confirmationObj = _merge(
              {},
              state.paymentConfirmationObj,
              this.getAutpayRowDataMap(state.paymentConfirmationObj)
            );
            break;

          case 'billAccount':
            this.confirmationObj = _merge(
              {},
              state.paymentConfirmationObj,
              this.getBillAccountsRowDataMap(state.paymentConfirmationObj)
            );
            break;
          default:
            this.confirmationObj = state.paymentConfirmationObj;
            break;
        }
      });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    // Defensive Loading spinner stop in case the user navigates away
    this.spinner.stop();
  }

  // Format the row / column data for scheduled payment confirmations
  private getScheduleRowDataMap(confirmationData: BillingPaymentConfirmationModel) {
    const namesArray = ['Account', 'Confirmation Number', 'Payment Details', 'Payment Method'];
    const propArray = ['account', 'confirmationNumber', 'paymentDetails', 'paymentMethod'];
    const isHeaderArray = [true, false, false, false];
    const valueArray = [];
    const columnArray = [];
    const templateRefMap = new Map();

    // Load the templates map
    templateRefMap.set('Account', this.accountNameCellTpl);
    templateRefMap.set('Confirmation Number', this.confirmationNumberCellTpl);
    templateRefMap.set('Payment Details', this.usdPaymentAmountWithDateCellTpl);

    // Set the payment account template
    const matchingPaymentAccount = this.paymentAccounts.find(
      paymentAccount => paymentAccount.nickName === confirmationData.paymentAccount
    );
    if (matchingPaymentAccount) {
      templateRefMap.set('Payment Method', this.paymentAccountCellTpl);
    } else {
      templateRefMap.set('Payment Method', this.paymentAccountNickNameOnlyCellTpl);
    }

    // Build values array
    confirmationData.billAccounts.forEach(billAccount => {
      valueArray.push({
        account: billAccount.billAccountNickName || billAccount.nickName,
        confirmationNumber: confirmationData.paymentConfirmationId,
        paymentAmount: billAccount.billAcctPaymentAmount,
        paymentDate: confirmationData.date,
        paymentDetails: {
          paymentAmount: billAccount.billAcctPaymentAmount,
          paymentDate: confirmationData.date
        },
        pastDueInfo: billAccount.pastDueInfo,
        matchingAutoPay: !!this.autoPayAccounts.find(
          autoPay => autoPay?.billAccountNumber === billAccount.billAccountNumber
        ),
        paymentMethod: matchingPaymentAccount
          ? matchingPaymentAccount
          : confirmationData.paymentAccount,
        useNickNameAsTitle: true
      });
    });

    return this.getTableMappedData(
      namesArray,
      columnArray,
      propArray,
      valueArray,
      confirmationData,
      templateRefMap,
      isHeaderArray
    );
  }

  // Format the row / column data for automatic payment confirmations
  private getAutpayRowDataMap(confirmationData: PaymentConfirmationModel) {
    const namesArray = ['Account', 'Payment Details', 'Payment Method'];
    const propArray = ['account', 'paymentDetails', 'paymentMethod'];
    const isHeaderArray = [true, false, false];
    const valueArray = [];
    const predictValueArray = [];
    const columnArray = [];
    const templateRefMap = new Map();

    templateRefMap.set('Account', this.accountNameCellTpl);
    templateRefMap.set('Payment Details', this.minOrFullPaymentAmountWithDaysBeforeCellTpl);
    templateRefMap.set('Payment Method', this.paymentAccountCellTpl);
    // Set the payment account template
    const matchingPaymentAccount = this.paymentAccounts.find(
      paymentAccount =>
        paymentAccount.nickName ===
        _get(confirmationData, 'payload.autopayRule[0].paymentAccountNickName')
    );
    for (const autopayRule of _get(confirmationData, 'payload.autopayRule', [])) {
      valueArray.push({
        account: _get(confirmationData, 'billAccountNickName'),
        paymentDetails: {
          amount: _get(autopayRule, 'paymentAmount'),
          daysBefore: _get(autopayRule, 'daysBeforeDueDate')
        },
        paymentMethod: matchingPaymentAccount
          ? matchingPaymentAccount
          : _get(autopayRule, 'paymentAccountNickName'),
        useNickNameAsTitle: true
      });
    }

    for (const autopayPredictRule of _get(confirmationData, 'payload.autopayPredictRule', [])) {
      if (autopayPredictRule) {
        predictValueArray.push({
          status: 'success',
          account: _get(confirmationData, 'billAccountNickName'),
          paymentDetails: {
            amount: _get(autopayPredictRule, 'predictedDollarAmount'),
            nextPaymentDate: _get(autopayPredictRule, 'nextPaymentDate')
          },
          paymentMethod: matchingPaymentAccount
            ? matchingPaymentAccount
            : autopayPredictRule.paymentAccountNickName,
          useNickNameAsTitle: true
        });
      }
    }

    /**
     * AS : Add the logic to push the data for autopay predict here.
     */

    return {
      autopayTableDetails: this.getTableMappedData(
        namesArray,
        columnArray,
        propArray,
        valueArray,
        confirmationData,
        templateRefMap,
        isHeaderArray
      ),
      autopayPredictTableDetails: this.getTableMappedData(
        namesArray,
        columnArray,
        propArray,
        predictValueArray,
        confirmationData,
        templateRefMap,
        isHeaderArray
      )
    };
  }

  // Format the row / column data for multiple automatic payment confirmations
  private getMultipleAutpayRowDataMap(confirmationData: PaymentConfirmationModel) {
    const namesArray = ['Status', 'Account', 'Payment Method', 'Payment Amount'];
    const propArray = ['status', 'account', 'paymentMethod', 'paymentDetails'];
    const isHeaderArray = [false, true, false, false];
    const hideArray = [true, false, false, false];
    const valueArray = [];
    const predictValueArray = [];
    const autoPayFailureArray = [];
    const columnArray = [];
    const templateRefMap = new Map();

    templateRefMap.set('Status', this.statusCheckCellTpl);
    templateRefMap.set('Account', this.accountNameCellTpl);
    templateRefMap.set('Payment Method', this.paymentAccountCellTpl);
    templateRefMap.set('Payment Amount', this.minOrFullPaymentAmountWithDaysBeforeCellTpl);
    // Set the payment account template
    const matchingPaymentAccount = this.paymentAccounts.find(
      paymentAccount =>
        paymentAccount.nickName === _get(confirmationData, 'accounts[0].paymentAccountNickName')
    );

    this.store
      .select(BillAccountsSelectors.selectBillAccountEntities)
      .pipe(take(1))
      .subscribe(billAccountsFromStore => {
        for (const autopayRule of _get(confirmationData, 'payload.autopayRule', [])) {
          valueArray.push({
            status: 'success',
            account: _get(
              billAccountsFromStore[autopayRule.billingNumber],
              'billingPreferences.accountNickName',
              autopayRule.billingNumber
            ),
            paymentDetails: {
              amount:
                autopayRule.paymentAmount === 'MINDUE'
                  ? 'Minimum Due'
                  : billAccountsFromStore[autopayRule.billingNumber].fullPayBalance > 0
                    ? 'Full Pay'
                    : 'Account Balance',
              daysBefore: autopayRule.daysBeforeDueDate
            },
            paymentMethod: matchingPaymentAccount
              ? matchingPaymentAccount
              : autopayRule.paymentAccountNickName,
            useNickNameAsTitle: true
          });
        }

        for (const autopayPredictRule of _get(confirmationData, 'payload.autopayPredictRule', [])) {
          if (autopayPredictRule) {
            predictValueArray.push({
              status: 'success',
              account: _get(
                billAccountsFromStore[autopayPredictRule.billAccountNumber],
                'billingPreferences.accountNickName',
                autopayPredictRule.billAccountNumber
              ),
              paymentDetails: {
                amount: _get(autopayPredictRule, 'predictedDollarAmount'),
                nextPaymentDate: _get(autopayPredictRule, 'nextPaymentDate')
              },
              paymentMethod: matchingPaymentAccount
                ? matchingPaymentAccount
                : autopayPredictRule.paymentAccountNickName,
              useNickNameAsTitle: true
            });
          }
        }

        for (const failedAutopayRule of _get(confirmationData, 'payload.failedAutoPayRules')) {
          if (failedAutopayRule) {
            autoPayFailureArray.push({
              status: 'failure',
              account: _get(
                billAccountsFromStore[failedAutopayRule.billingNumber],
                'billingPreferences.accountNickName',
                failedAutopayRule.billingNumber
              ),
              paymentDetails: {
                amount:
                  failedAutopayRule.paymentAmount === 'MINDUE'
                    ? 'Minimum Due'
                    : billAccountsFromStore[failedAutopayRule.billingNumber].fullPayBalance > 0
                      ? 'Full Pay'
                      : 'Account Balance',
                daysBefore: failedAutopayRule.daysBeforeDueDate
              },
              paymentMethod: matchingPaymentAccount
                ? matchingPaymentAccount
                : failedAutopayRule.paymentAccountNickName,
              useNickNameAsTitle: true
            });
          }
        }
      });

    /**
     * AS : Add the logic to push the data for autopay predict here.
     */

    return {
      autopayTableDetails: this.getTableMappedData(
        namesArray,
        columnArray,
        propArray,
        valueArray,
        confirmationData,
        templateRefMap,
        isHeaderArray,
        hideArray
      ),
      autopayPredictTableDetails: this.getTableMappedData(
        namesArray,
        columnArray,
        propArray,
        predictValueArray,
        confirmationData,
        templateRefMap,
        isHeaderArray,
        hideArray
      ),
      autopayFailedTableDetails: this.getTableMappedData(
        namesArray,
        columnArray,
        propArray,
        autoPayFailureArray,
        confirmationData,
        templateRefMap,
        isHeaderArray,
        hideArray
      )
    };
  }
  // Format the row / column data from payment methods confirmations
  private getPaymentMethodsRowDataMap(confirmationData: PaymentConfirmationModel) {
    const namesArray = ['Payment Method Name', 'Account Details'];
    const propArray = ['paymentMethod', 'account'];
    const isHeaderArray = [true, false];
    const valueArray = [];
    const columnArray = [];
    const templateRefMap = new Map();

    templateRefMap.set('Payment Method Name', this.accountNameCellTpl);
    templateRefMap.set('Account Details', this.paymentAccountCellTpl);

    valueArray.push({
      account: confirmationData.paymentMethod.nickName,
      paymentMethod: confirmationData.paymentMethod,
      useNickNameAsTitle: false
    });

    return this.getTableMappedData(
      namesArray,
      columnArray,
      propArray,
      valueArray,
      confirmationData,
      templateRefMap,
      isHeaderArray
    );
  }

  // Format the row / column data for registration / unregistration confirmations
  private getBillAccountsRowDataMap(confirmationData: PaymentConfirmationModel) {
    const isHeaderArray = [true, false, false];
    const valueArray = [];
    const columnArray = [];
    const templateRefMap = new Map();
    templateRefMap.set('Account', this.accountNameCellTpl);

    let columnTitle = '';
    if (
      _has(confirmationData, 'riskDescription.length') &&
      confirmationData.riskDescription.length > 0
    ) {
      templateRefMap.set('Risk Description', this.riskDescCellTpl);
      columnTitle = 'Risk Description';
    } else {
      templateRefMap.set('Policy Description', this.policyDescriptionCellTpl);
      columnTitle = 'Policy Description';
    }

    const namesArray = ['Account', columnTitle];
    const propArray = ['account', 'billAccountNumber'];

    valueArray.push({
      account: confirmationData.nickName,
      billAccountNumber: new PrettyBillingAcctNum().transform(confirmationData.billAccountNumber),
      riskDescription: confirmationData.riskDescription,
      policyDescription: confirmationData.policyDescription
    });

    return this.getTableMappedData(
      namesArray,
      columnArray,
      propArray,
      valueArray,
      confirmationData,
      templateRefMap,
      isHeaderArray
    );
  }

  private getTableMappedData(
    namesArray,
    columnArray,
    propArray,
    valueArray,
    confirmationData,
    templateRefMap,
    isHeaderArray,
    hideArray = []
  ) {
    const columnMap: Map<string, string> = new Map();
    const columnsData = [];
    namesArray.forEach((key, index) => {
      columnMap.set(key, propArray[index]);
      // AS: Add the column if the data exists, else do not show the empty column
      if (_find(valueArray, data => !!data[propArray[index]])) {
        columnsData.push({
          cellTemplate: templateRefMap.get(key),
          headerTemplate: this.headerCellTpl,
          isHeaderOnMobile: isHeaderArray[index],
          hideOnMobile: hideArray[index] ? hideArray[index] : false,
          name: key,
          prop: propArray[key],
          draggable: false,
          resizeable: false,
          sortable: false
        });
      }
    });

    return {
      columnNamePropMap: columnsData,
      rowsPropValueMap: valueArray,
      confirmationMsgHeading: confirmationData.subCategory + 'PaymentsConfirmationHeading'
    };
  }

  private formatStoreData(storeConfirmationData: PaymentConfirmationModel) {
    return {
      transactionId: storeConfirmationData.confirmationId,
      subCategory: storeConfirmationData.subCategory,
      category: storeConfirmationData.category,
      accountName: storeConfirmationData.paymentMethod.nickName
    };
  }
}
